import { State } from 'src/replicant/State';
import {
  MagicLampsOutcomeId,
  MagicLampsPrizeId,
  MagicLampsOutcome,
  magicLampsEvent,
  MagicLampsRewardId,
  magicLampsContinueCost,
  MagicLampsTuning,
  roundsTuning,
  magicLampsSchedule,
  magicLampsUnlockLevel,
} from './magiclamps.ruleset';
import { createPrng } from '@play-co/replicant';
import {
  getRandomItemFromArray,
  shuffleArray,
  weightedRandom,
} from 'src/replicant/utils/random';
import { Match2Reward } from '../rewards/match2-rewards.ruleset';
import { duration } from 'src/replicant/utils/duration';

/**
 * Check if magic lamps feature is active and if there is an ongoing event.
 */
export function magicLampsIsAvailable(state: State, now: number): boolean {
  // Only available if player is above level 33
  if (state.match2.level < magicLampsUnlockLevel) return false;

  // Get current ongoing event
  const event = magicLampsGetOngoingEvent(now);

  return !!event;
}

/**
 * Get current ongoing event or null if there is none.
 * Event is recurring every week based on the schedule of the original event.
 */
export function magicLampsGetOngoingEvent(now: number) {
  const event = magicLampsSchedule.getCurrentEvent(now);
  if (!event) return null;

  // Get first scheduled start
  const first = new Date(magicLampsEvent.startAt).getTime();

  // Get a single day duration in ms
  const day = duration({ days: 1 });

  // Find the days difference from now and the first schedule
  const dayDiff = Math.floor((now - first) / day);

  // Calculate the week number based of the difference from now to first schedule
  const weekNum = Math.floor(dayDiff / 7);

  // Return null if the date is before the first tournament
  if (weekNum < 0) return null;

  // Magic lamps event is active
  return {
    ...magicLampsEvent,
    id: `magicLamps-${weekNum}`,
    startAt: event.start,
    endAt: event.end,
  };
}

/**
 * Return last time user has seen the magic lamps popup.
 */
export function magicLampsGetLastViewedAt(state: State): number {
  return state.match2.magicLamps?.lastViewedAt ?? 0;
}

/**
 * Get the rounds of the event as an array.
 */
function getEventRounds(state: State) {
  const tuning: MagicLampsTuning = 'control';
  const rounds = roundsTuning[tuning];
  const keys = Object.keys(rounds).sort();
  return keys.map((key) => rounds[key]);
}

/**
 * Get a single event round by round number: rounds are 1-indexed (starting from 1).
 */
function getEventRound(state: State, index: number) {
  return getEventRounds(state)[index - 1];
}

/**
 * Get the number of rounds in the event.
 */
function magicLampsGetNumRounds(state: State) {
  return getEventRounds(state).length;
}

/**
 * Convert outcome into a prize. Trap is also considered a 'prize' in this context.
 */
function getPrizeByOutcome(
  type: MagicLampsOutcomeId,
  value = 0,
  prng: () => number,
): { type: MagicLampsPrizeId; value: number } {
  if (type === 'trap') {
    return { type: 'trap', value: 0 };
  }

  if (type === 'typeA' || type === 'typeB' || type === 'typeC') {
    const table = magicLampsEvent.extraRewards[type];
    const types = Object.keys(table);
    const selected = getRandomItemFromArray(types, prng);
    return {
      type: selected as MagicLampsPrizeId,
      value: (table as Record<string, number>)[selected],
    };
  }

  return { type, value };
}

/**
 * Get a list of prizes from a list of outcomes.
 */
function getPrizesFromOutcomes(
  outcomes: MagicLampsOutcome[],
  multiplier: number,
  prng: () => number,
) {
  const prizes = [];

  for (let i = 0; i < outcomes.length; i++) {
    const outcome = outcomes[i];
    const prize = getPrizeByOutcome(outcome.type, outcome.value, prng);
    if (multiplier) {
      prize.value = Math.ceil(prize.value * multiplier);
    }
    prizes.push(prize);
  }

  return prizes;
}

/**
 * Returns an array of prizes for the given round and seed. The first item is the selected prize.
 */
function magicLampsGetPrizesByRound(
  state: State,
  round: number,
  seed: string,
  noTraps: boolean,
) {
  const roundData = getEventRound(state, round);

  if (!roundData) return [];

  let outcomes = roundData.outcomes.slice();

  if (noTraps) {
    // Filter out traps if noTraps is true
    outcomes = outcomes.filter((item) => item.type !== 'trap');
  }

  const prng = createPrng(`${seed}-${round}`);
  const weights = outcomes.map((item) => {
    return item.chance;
  });
  const index = weightedRandom(weights, prng);
  const prizes = getPrizesFromOutcomes(outcomes, roundData.multiplier, prng);
  const selected = prizes.splice(index, 1)[0];
  const unselected = prizes.slice();
  shuffleArray(unselected, prng);

  return [selected, ...unselected];
}

/**
 * Get current prizes for the minigame. Always return the same result for the same round and seed.
 */
export function magicLampsGetCurrentPrizes(
  state: State,
): { type: MagicLampsPrizeId; value: number }[] | null {
  const magicLamps = state.match2.magicLamps;
  if (!magicLamps) return null;
  const round = magicLamps.round;
  const noTraps = !!magicLamps.continuing;
  const seed = `${state.id}-${magicLamps.eventId}-${magicLamps.numPlays}`;
  return magicLampsGetPrizesByRound(state, round, seed, noTraps);
}

export function magicLampsGetRemainingTime(now: number): number {
  const event = magicLampsGetOngoingEvent(now);
  if (!event) return 0;
  const remainingTime = event.endAt - now;
  return remainingTime > 0 ? remainingTime : 0;
}

export function magicLampsGetCurrentRound(state: State): number {
  return state.match2.magicLamps?.round ?? 1;
}

export function magicLampsGetCurrentStage(state: State): number {
  const round = magicLampsGetCurrentRound(state);
  const stage = getEventRound(state, round)?.stage ?? 0;
  return stage;
}

export function magicLampsGetPreviousStage(state: State): number {
  const round = magicLampsGetCurrentRound(state);
  const stage = getEventRound(state, round - 1)?.stage;
  return stage ?? 0;
}

export function magicLampsGetStages(
  state: State,
): Record<number, { first: number; last: number }> {
  const stages: Record<number, { first: number; last: number }> = {};
  const rounds = getEventRounds(state);

  for (let i = 0; i < rounds.length; i++) {
    const round = rounds[i];
    const roundIndex = i + 1;
    if (!stages[round.stage]) {
      stages[round.stage] = {
        first: roundIndex,
        last: roundIndex,
      };
    } else {
      if (roundIndex > stages[round.stage].last) {
        stages[round.stage].last = roundIndex;
      }
    }
  }

  return stages;
}

export function magicLampsGetContinueCost(state: State): number {
  const continuesUsed = state.match2.magicLamps?.continuesUsed ?? 0;
  const index = Math.min(continuesUsed, magicLampsContinueCost.length - 1);

  return magicLampsContinueCost[index];
}

export function magicLampsIsCompleted(state: State): boolean {
  const round = magicLampsGetCurrentRound(state);
  return round > magicLampsGetNumRounds(state);
}

export function magicLampsGetEntryCost(state: State): number {
  const numPlays = state.match2.magicLamps?.numPlays ?? 0;
  return numPlays < 1 ? 0 : magicLampsEvent.entryCost;
}

export function magicLampsGetGrandPrize(): number {
  return magicLampsEvent.grandPrize;
}

export function magicLampsGetAccumulatedRewards(
  state: State,
): Record<MagicLampsRewardId, number> {
  const rewards = state.match2?.magicLamps?.rewards ?? {};
  return rewards as Record<MagicLampsRewardId, number>;
}

/**
 * Convert current accumulated rewards into a reward set.
 */
export function magicLampsGetRewardSet(state: State): Match2Reward[] {
  if (!state.match2.magicLamps) return [];

  const accumulated = state.match2.magicLamps.rewards;

  if (!accumulated) return [];

  const rewards: Match2Reward[] = [];

  for (const [id, value] of Object.entries(accumulated)) {
    const reward = magicLampsGetRewardFromItem(id as MagicLampsRewardId, value);
    if (reward) rewards.push(reward);
  }

  return rewards;
}

export function magicLampsGetRewardFromItem(
  id: MagicLampsRewardId,
  value: number,
): Match2Reward | null {
  switch (id) {
    case 'unlimitedEnergy':
      return {
        type: 'energy',
        duration: value,
      };

    case 'unlimitedRocket':
      return {
        type: 'powerups',
        duration: value,
        subType: 'rocket',
      };

    case 'unlimitedBomb':
      return {
        type: 'powerups',
        duration: value,
        subType: 'bomb',
      };

    case 'gems':
      return {
        type: 'gems',
        amount: value,
      };
    case 'energy':
      return {
        type: 'energy',
        amount: value,
      };
    case 'points':
      return {
        type: 'bPoints',
        amount: value,
      };

    case 'bomb':
    case 'rocket':
    case 'cube':
      return {
        type: 'powerups',
        amount: value,
        subType: id,
      };

    case 'grandPrize':
      return {
        type: 'gems',
        amount: value,
      };

    case 'dart':
    case 'bullet':
    case 'drill':
    case 'roulette':
      return {
        type: 'boosters',
        amount: value,
        subType: id,
      };
  }

  return null;
}
/**
 * Check if user has game in progress.
 * A game is only considered in progress if the event is still available.
 */
export function magicLampsHasGameInProgress(
  state: State,
  now: number,
): boolean {
  if (!state.match2.magicLamps?.playing) {
    return false;
  }

  return magicLampsIsAvailable(state, now);
}
